import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Lock, Calendar, AlertOctagon, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Three governance action dialogs (Phase 4):
 *   • LockDialog        — "Lock as Brand Standard" with impact preview
 *   • ScheduleDialog    — "Schedule Change" with date/time picker
 *   • BreakGlassDialog  — "Emergency Override" with typed reason + ack
 *
 * Each dialog:
 *   1. Fetches the propagation impact when opened (so the operator sees
 *      EXACTLY what they're about to affect)
 *   2. Posts the action via api
 *   3. On success, returns the propagation summary to the caller via
 *      `onSuccess(summary, action)` so an inline success card can render
 *
 * Trilingual EN/FR/ES via i18n keys throughout.
 */

const ModalShell = ({ icon: Icon, titleKey, onClose, children, "data-testid": testId }) => {
    const { t } = useTranslation();
    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid={testId}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.6)" }}
            onClick={onClose}
        >
            <div
                role="document"
                onClick={(e) => e.stopPropagation()}
                className="ob-card max-w-lg w-full p-6 sm:p-8 space-y-5 max-h-[85vh] overflow-y-auto"
                style={{ background: "var(--ob-surface-elevated)" }}
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="ob-tile-icon" aria-hidden="true">
                            <Icon size={20} strokeWidth={1.5} />
                        </div>
                        <h2
                            className="text-2xl"
                            style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}
                            data-testid="ob-gov-dialog-title"
                        >
                            {t(titleKey)}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="p-1.5 rounded-lg hover:bg-white/5"
                        style={{ color: "var(--ob-text-tertiary)" }}
                    >
                        <X size={18} strokeWidth={1.5} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
};

const ImpactBlock = ({ orgId, moduleName, displayName }) => {
    const { t } = useTranslation();
    const [impact, setImpact] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(`/operational-base/organizations/${orgId}/modules/${moduleName}/propagation-impact`);
                if (!cancelled) setImpact(res.data);
            } catch (_) {
                if (!cancelled) setImpact({ descendant_org_count: 0, region_count: 0, location_count: 0, active_break_glass_count: 0, existing_lock: null });
            }
        })();
        return () => { cancelled = true; };
    }, [orgId, moduleName]);

    if (!impact) {
        return <p className="text-sm" style={{ color: "var(--ob-text-tertiary)" }}>{t("ob.activity.loading")}</p>;
    }
    const existing = impact.existing_lock;
    return (
        <div className="space-y-3">
            {existing && (
                <div
                    data-testid="ob-gov-existing-lock-warning"
                    className="rounded-xl p-4 text-sm"
                    style={{ background: "rgba(59,130,246,0.07)", border: "1px solid rgba(59,130,246,0.20)", color: "#93C5FD" }}
                >
                    <p className="font-medium mb-1" style={{ color: "#FDE68A" }}>
                        {t("ob.governance.lock.replacement_heading")}
                    </p>
                    <p style={{ color: "var(--ob-text-secondary)" }}>
                        {t(
                            existing.actor_name
                                ? "ob.governance.lock.replacement_body_with_actor"
                                : "ob.governance.lock.replacement_body_no_actor",
                            {
                                provenance: t(`ob.governance.lock.provenance_${existing.provenance || "operational"}`),
                                actor: existing.actor_name || "",
                                when: existing.locked_at_relative || "",
                                reason: existing.reason || t("ob.governance.lock.replacement_no_reason"),
                            },
                        )}
                    </p>
                    <p className="text-xs mt-2" style={{ color: "var(--ob-text-tertiary)" }}>
                        {t("ob.governance.lock.replacement_archive_note")}
                    </p>
                </div>
            )}
            <div
                data-testid="ob-gov-impact-block"
                className="rounded-xl p-4 space-y-2 text-sm"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
                <p style={{ color: "var(--ob-text-secondary)" }}>
                    {t("ob.governance.impact.intro", { name: displayName })}
                </p>
                <ul className="space-y-1">
                    {impact.descendant_org_count === 0 ? (
                        <li style={{ color: "var(--ob-text-secondary)" }}>
                            · {t("ob.governance.impact.local_only")}
                        </li>
                    ) : (
                        <>
                            {impact.inheriting_count > 0 && (
                                <li style={{ color: "var(--ob-text-secondary)" }}>
                                    · {t("ob.governance.impact.inheriting", { count: impact.inheriting_count })}
                                </li>
                            )}
                            {impact.overriding_count > 0 && (
                                <li style={{ color: "#A7F3D0" }}>
                                    · {t("ob.governance.impact.overriding_remain", { count: impact.overriding_count })}
                                </li>
                            )}
                            {impact.region_count > 0 && impact.location_count > 0 && (
                                <li style={{ color: "var(--ob-text-tertiary)" }}>
                                    · {t("ob.governance.impact.shape", {
                                        regions: impact.region_count,
                                        locations: impact.location_count,
                                    })}
                                </li>
                            )}
                        </>
                    )}
                    {impact.active_break_glass_count > 0 && (
                        <li style={{ color: "#93C5FD" }}>
                            · {t("ob.governance.impact.break_glass", { count: impact.active_break_glass_count })}
                        </li>
                    )}
                </ul>
                <p className="text-xs pt-1" style={{ color: "var(--ob-text-tertiary)" }}>
                    {t("ob.governance.impact.local_message", { name: displayName })}
                </p>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────
// LOCK DIALOG
// ─────────────────────────────────────────────────────────────────────
export const LockDialog = ({ orgId, module, displayName, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const [provenance, setProvenance] = useState("operational");
    const [breakGlassEligible, setBgEligible] = useState(true);
    const [reason, setReason] = useState("");
    const [enabled, setEnabled] = useState(true);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            const res = await api.post(
                `/operational-base/organizations/${orgId}/modules/${module}/lock`,
                { enabled, provenance, break_glass_eligible: breakGlassEligible, reason: reason.trim() || null },
            );
            toast.success(t("ob.governance.lock.success_toast"));
            onSuccess && onSuccess({ propagation: res.data?.propagation, action: "lock", module: res.data?.module });
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.governance.lock.error_toast"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <ModalShell icon={Lock} titleKey="ob.governance.lock.title" onClose={onClose} data-testid="ob-gov-lock-dialog">
            <p style={{ color: "var(--ob-text-secondary)" }}>
                {t("ob.governance.lock.subtitle", { name: displayName })}
            </p>
            <ImpactBlock orgId={orgId} moduleName={module} displayName={displayName} />

            <div className="space-y-3">
                <fieldset>
                    <legend className="ob-eyebrow mb-2">{t("ob.governance.lock.value_legend")}</legend>
                    <div className="flex gap-2">
                        {[{ k: true, label: t("ob.governance.lock.value_enabled") }, { k: false, label: t("ob.governance.lock.value_disabled") }].map((o) => (
                            <button key={String(o.k)} type="button" onClick={() => setEnabled(o.k)}
                                data-testid={`ob-gov-lock-value-${o.k}`}
                                className="flex-1 px-3 py-2 rounded-xl text-sm transition-colors border"
                                style={{
                                    background: enabled === o.k ? "rgba(16,185,129,0.10)" : "transparent",
                                    color: enabled === o.k ? "var(--ob-emerald)" : "var(--ob-text-secondary)",
                                    borderColor: enabled === o.k ? "rgba(16,185,129,0.30)" : "rgba(255,255,255,0.08)",
                                }}>
                                {o.label}
                            </button>
                        ))}
                    </div>
                </fieldset>

                <fieldset>
                    <legend className="ob-eyebrow mb-2">{t("ob.governance.lock.provenance_legend")}</legend>
                    <div className="grid grid-cols-3 gap-2">
                        {["operational", "regulatory", "safety"].map((p) => (
                            <button key={p} type="button" onClick={() => setProvenance(p)}
                                data-testid={`ob-gov-lock-prov-${p}`}
                                className="px-3 py-2 rounded-xl text-sm transition-colors border"
                                style={{
                                    background: provenance === p ? "rgba(255,255,255,0.06)" : "transparent",
                                    color: provenance === p ? "var(--ob-text-primary)" : "var(--ob-text-secondary)",
                                    borderColor: provenance === p ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.06)",
                                }}>
                                {t(`ob.governance.lock.provenance_${p}`)}
                            </button>
                        ))}
                    </div>
                </fieldset>

                <label className="flex items-center gap-2 text-sm" style={{ color: "var(--ob-text-secondary)" }}>
                    <input
                        type="checkbox"
                        checked={breakGlassEligible && provenance === "operational"}
                        disabled={provenance !== "operational"}
                        onChange={(e) => setBgEligible(e.target.checked)}
                        data-testid="ob-gov-lock-bg-checkbox"
                    />
                    {t("ob.governance.lock.allow_break_glass")}
                </label>

                <label className="block">
                    <span className="ob-eyebrow">{t("ob.governance.lock.reason_label")}</span>
                    <input
                        type="text"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={t("ob.governance.lock.reason_placeholder")}
                        data-testid="ob-gov-lock-reason"
                        className="mt-1 w-full px-3 py-2 rounded-xl text-sm"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)" }}
                    />
                </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm"
                    style={{ color: "var(--ob-text-secondary)" }}>
                    {t("common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button type="button" onClick={submit} disabled={busy} className="ob-emerald-btn"
                    data-testid="ob-gov-lock-confirm">
                    {busy ? t("common.loading") : t("ob.governance.lock.confirm")}
                </button>
            </div>
        </ModalShell>
    );
};

// ─────────────────────────────────────────────────────────────────────
// SCHEDULE DIALOG
// ─────────────────────────────────────────────────────────────────────
export const ScheduleDialog = ({ orgId, module, displayName, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const [enabled, setEnabled] = useState(true);
    const [startAt, setStartAt] = useState("");
    const [endAt, setEndAt] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!startAt) { toast.error(t("ob.governance.schedule.missing_start")); return; }
        setBusy(true);
        try {
            const res = await api.post(
                `/operational-base/organizations/${orgId}/modules/${module}/schedule`,
                {
                    enabled,
                    start_at: new Date(startAt).toISOString(),
                    end_at: endAt ? new Date(endAt).toISOString() : null,
                    reason: reason.trim() || null,
                },
            );
            toast.success(t("ob.governance.schedule.success_toast"));
            onSuccess && onSuccess({ action: "schedule", module: res.data?.module });
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.governance.schedule.error_toast"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <ModalShell icon={Calendar} titleKey="ob.governance.schedule.title" onClose={onClose} data-testid="ob-gov-schedule-dialog">
            <p style={{ color: "var(--ob-text-secondary)" }}>
                {t("ob.governance.schedule.subtitle", { name: displayName })}
            </p>

            <fieldset>
                <legend className="ob-eyebrow mb-2">{t("ob.governance.schedule.value_legend")}</legend>
                <div className="flex gap-2">
                    {[{ k: true, label: t("ob.governance.lock.value_enabled") }, { k: false, label: t("ob.governance.lock.value_disabled") }].map((o) => (
                        <button key={String(o.k)} type="button" onClick={() => setEnabled(o.k)}
                            data-testid={`ob-gov-schedule-value-${o.k}`}
                            className="flex-1 px-3 py-2 rounded-xl text-sm transition-colors border"
                            style={{
                                background: enabled === o.k ? "rgba(16,185,129,0.10)" : "transparent",
                                color: enabled === o.k ? "var(--ob-emerald)" : "var(--ob-text-secondary)",
                                borderColor: enabled === o.k ? "rgba(16,185,129,0.30)" : "rgba(255,255,255,0.08)",
                            }}>{o.label}</button>
                    ))}
                </div>
            </fieldset>

            <label className="block">
                <span className="ob-eyebrow">{t("ob.governance.schedule.start_label")}</span>
                <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)}
                    data-testid="ob-gov-schedule-start"
                    className="mt-1 w-full px-3 py-2 rounded-xl text-sm"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)", colorScheme: "dark" }} />
            </label>
            <label className="block">
                <span className="ob-eyebrow">{t("ob.governance.schedule.end_label")}</span>
                <input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)}
                    data-testid="ob-gov-schedule-end"
                    className="mt-1 w-full px-3 py-2 rounded-xl text-sm"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)", colorScheme: "dark" }} />
            </label>
            <label className="block">
                <span className="ob-eyebrow">{t("ob.governance.lock.reason_label")}</span>
                <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder={t("ob.governance.schedule.reason_placeholder")}
                    data-testid="ob-gov-schedule-reason"
                    className="mt-1 w-full px-3 py-2 rounded-xl text-sm"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)" }} />
            </label>

            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm"
                    style={{ color: "var(--ob-text-secondary)" }}>{t("common.cancel", { defaultValue: "Cancel" })}</button>
                <button type="button" onClick={submit} disabled={busy} className="ob-emerald-btn"
                    data-testid="ob-gov-schedule-confirm">
                    {busy ? t("common.loading") : t("ob.governance.schedule.confirm")}
                </button>
            </div>
        </ModalShell>
    );
};

// ─────────────────────────────────────────────────────────────────────
// BREAK-GLASS DIALOG
// ─────────────────────────────────────────────────────────────────────
export const BreakGlassDialog = ({ orgId, module, displayName, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState("");
    const [duration, setDuration] = useState(60);
    const [acknowledged, setAck] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [busy, setBusy] = useState(false);

    const canSubmit = reason.trim().length > 3 && acknowledged && duration > 0;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            const res = await api.post(
                `/operational-base/organizations/${orgId}/modules/${module}/break-glass`,
                { reason: reason.trim(), duration_minutes: Number(duration), enabled, acknowledged: true },
            );
            toast.success(t("ob.governance.break_glass.success_toast", { duration }));
            onSuccess && onSuccess({ action: "break_glass", module: res.data?.module, expires_at: res.data?.expires_at });
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.governance.break_glass.error_toast"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <ModalShell icon={AlertOctagon} titleKey="ob.governance.break_glass.title" onClose={onClose} data-testid="ob-gov-break-glass-dialog">
            <div
                className="rounded-xl p-4 text-sm"
                style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.20)", color: "#93C5FD" }}
            >
                {t("ob.governance.break_glass.warning", { name: displayName })}
            </div>

            <label className="block">
                <span className="ob-eyebrow">{t("ob.governance.break_glass.reason_label")}</span>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                    placeholder={t("ob.governance.break_glass.reason_placeholder")}
                    data-testid="ob-gov-break-glass-reason"
                    className="mt-1 w-full px-3 py-2 rounded-xl text-sm"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)" }} />
            </label>

            <div className="grid grid-cols-2 gap-3">
                <label className="block">
                    <span className="ob-eyebrow">{t("ob.governance.break_glass.duration_label")}</span>
                    <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}
                        data-testid="ob-gov-break-glass-duration"
                        className="mt-1 w-full px-3 py-2 rounded-xl text-sm"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--ob-text-primary)" }}>
                        <option value={30}>30 min</option>
                        <option value={60}>1 h</option>
                        <option value={180}>3 h</option>
                        <option value={480}>8 h</option>
                        <option value={1440}>24 h</option>
                    </select>
                </label>
                <fieldset>
                    <legend className="ob-eyebrow mb-2">{t("ob.governance.break_glass.value_legend")}</legend>
                    <div className="flex gap-2">
                        {[{ k: false, label: t("ob.governance.lock.value_disabled") }, { k: true, label: t("ob.governance.lock.value_enabled") }].map((o) => (
                            <button key={String(o.k)} type="button" onClick={() => setEnabled(o.k)}
                                data-testid={`ob-gov-bg-value-${o.k}`}
                                className="flex-1 px-3 py-2 rounded-xl text-xs border"
                                style={{
                                    background: enabled === o.k ? "rgba(59,130,246,0.10)" : "transparent",
                                    color: enabled === o.k ? "#93C5FD" : "var(--ob-text-secondary)",
                                    borderColor: enabled === o.k ? "rgba(59,130,246,0.30)" : "rgba(255,255,255,0.08)",
                                }}>{o.label}</button>
                        ))}
                    </div>
                </fieldset>
            </div>

            <label className="flex items-start gap-2 text-sm" style={{ color: "var(--ob-text-secondary)" }}>
                <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAck(e.target.checked)}
                    data-testid="ob-gov-break-glass-ack"
                    className="mt-1"
                />
                {t("ob.governance.break_glass.ack")}
            </label>

            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm"
                    style={{ color: "var(--ob-text-secondary)" }}>{t("common.cancel", { defaultValue: "Cancel" })}</button>
                <button type="button" onClick={submit} disabled={busy || !canSubmit}
                    className="px-5 py-2.5 rounded-full text-sm font-medium"
                    style={{ background: canSubmit ? "#3B82F6" : "rgba(59,130,246,0.40)", color: "#0A0B0E" }}
                    data-testid="ob-gov-break-glass-confirm">
                    {busy ? t("common.loading") : t("ob.governance.break_glass.confirm")}
                </button>
            </div>
        </ModalShell>
    );
};
